import { environment as env } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { BehaviorSubject, catchError, finalize, map, of, take, tap } from 'rxjs';
import { ErrorHandlerService } from './error-handler/error-handler.service';
import { ApiService } from './api.service';
import { LocalStorageService } from './local-storage.service';
import { Location } from '@angular/common';
import { LOCALSTORAGE_KEYS } from './constants/databases';
import { Session } from './types/session.type';
import { RequestHandlerParams } from './types/api-service.types';
import { AccountSettings, DeliveryOptionsResponseItem, EmailExistenceResponse, ExplorerUserPayload, NewAccountPayload, normalizeECommercePermissionsResponse, PaymentDataPayload, PaymentMethodResponse } from './types/account.types';
import { ApiResponse } from './common/types';
import { MarketingReferral } from './types/common.types';
import { SignalsStoreService } from './signals-store.service';
import { KlaviyoService } from './klaviyo/klaviyo.service';
import { NotificationService } from './notification/notification.service';
import { HTTP_STATUS_CODES } from './constants/errors';
// import { OrderService } from './order.service';
import { PastDueService } from './past-due.service';

export interface PaymentMethod {
  address?: string;
  billingAddressCheck?: boolean;
  additionalAddress?: string;
  cardCode: string;
  firstName: string;
  lastName: string;
  cardNumber: string;
  city?: string;
  email: string;
  expirationDate: string;
  state?: string;
  zipCode?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private apiService = inject(ApiService)
  private localStorageService = inject(LocalStorageService)
  private location = inject(Location)
  private signalsStoreService = inject(SignalsStoreService);
  #notificationService = inject(NotificationService);
  #klaviyoService = inject(KlaviyoService);
  #pastDueService = inject(PastDueService);

  endpoints = {
    paymentMethod: `/payment-method`,
    prospect: `${env.apis.v3}/prospect`,
    base: `/account`,
    deliveryOptions: '/delivery-options',
    pickUpOption: `/pickup-locations`,
    settings: '/settings',
    customerTypes: `/customer-types`,
    stockNotification: '/stock-notification',
    ecommercePermissions: '/ecommerce-permissions',
    signupVerification: '/signup/verification',
    explorer: '/explorer',
    activateExplorer: '/explorer/activate'
  }
  isLoading: WritableSignal<boolean> = signal(false);

  private mustFetchStatesSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  mustFetchStates$ = this.mustFetchStatesSubject.asObservable();

  /** Explorer flow (DEV-11262). The explorer is born in a modal, not on a page
   *  load, so the header has no reason to re-read the session. It listens here
   *  instead and swaps to the signed-in state without a reload. */
  private explorerAccountCreatedSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  explorerAccountCreated$ = this.explorerAccountCreatedSubject.asObservable();
  setExplorerAccountCreated = (value: boolean) => this.explorerAccountCreatedSubject.next(value);

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService
  ) { }

  create(data: NewAccountPayload) {
    this.isLoading.set(true);
    let body = data;
    const marketingReferralData = this.localStorageService.get(LOCALSTORAGE_KEYS.MARKETING_REFERRAL) as MarketingReferral;
    if (marketingReferralData && Object.keys(marketingReferralData).length) {
      body = {
        ...body,
        marketingReferralData
      };
    }
    const params: RequestHandlerParams = {
      endpoint: this.endpoints.base,
      method: 'POST',
      apiV3: true,
      body,
      returnError: true,
      showErrorMessage: false
    };

    return this.apiService.handleRequest<ApiResponse<Session>>(params).pipe(
      tap(response => {
        if (response.error && response.status !== HTTP_STATUS_CODES.CONFLICT)
          this.#notificationService.show({ text: response.message, type: 'error' });
      }),
      tap(response => {
        if (!!response.data && !!!response.data.isTimeSlotCapacityFull) {
          this.localStorageService.set(LOCALSTORAGE_KEYS.SESSION, { ...response.data });
          this.signalsStoreService.hasSession.set(true);
          this.getMembershipPermissions().subscribe();
          this.#klaviyoService.identiyUser();
        }
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  addPaymentMethod(body: PaymentDataPayload, newUser: boolean = true) {
    const params: RequestHandlerParams = {
      method: 'POST',
      endpoint: `${this.endpoints.base}${this.endpoints.paymentMethod}`,
      body,
      showErrorMessage: true,
      apiV3: true
    }

    this.isLoading.set(true);

    return this.apiService.handleRequest<ApiResponse<PaymentMethodResponse>>(params)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        tap((response) => {
          if (response?.data) {
            const { settings, card: creditCard } = response.data;
            this.signalsStoreService.updateSession({ settings, creditCard });
            this.localStorageService.set(LOCALSTORAGE_KEYS.FIRST_LOGIN, !!settings.firstLogin);
            // A past-due customer reaches this call from the warning's CTA, and a
            // saved card is the answer to it: the balance is retried against this
            // card, so there is nothing left for the warning to ask for. If the
            // retry fails, the next login brings the flag back with the session
            // payload (DEV-10988). `inProcess` is the card update itself still
            // settling, which is too early to call it answered. This runs before
            // the signup branch below, which reloads the page.
            const inProcess = !!(response as unknown as { inProcess?: boolean }).inProcess;
            if (!inProcess) this.#pastDueService.clear();

            if (newUser) {
              this.localStorageService.set(LOCALSTORAGE_KEYS.NEW_ACCOUNT, true);
              this.location.replaceState('/shop');
              window.location.reload();
            }
          }
        })
      )
  }

  addProspect(clientInfo: {
    firstName: string;
    lastName: string;
    email: string;
    latitude: string;
    longitude: string;
    address: string;
    city: string;
    stateCode: string;
    zipCode: string;
  }) {
    const apiUrl = this.endpoints.prospect;
    const bodyRequest = {
      ...clientInfo
    }
    this.isLoading.set(true);

    return this.http.post<any>(apiUrl, bodyRequest).pipe(
      catchError((error) => this.errorHandlerService.handleHttpError(error)),
      take(1),
      finalize(() => {
        console.log('finalized');
        this.isLoading.set(false);
      })
    )

  }

  getDeliveryOptions(zoneId?: number) {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.base}${this.endpoints.deliveryOptions}${zoneId ? `?zoneId=${zoneId}` : ''}`,
      method: 'GET',
      apiV3: true
    }
    return this.apiService.handleRequest<ApiResponse<DeliveryOptionsResponseItem[]>>(params).pipe(
      map((response: ApiResponse<DeliveryOptionsResponseItem[]>) => this.setUpDeliveryOptionsResponse(response.data))
    );
  }

  private setUpDeliveryOptionsResponse(data: DeliveryOptionsResponseItem[]): DeliveryOptionsResponseItem[] {
    // Mapping of days of the week to numeric values
    const weekOrder: any = {
      'monday': 0,
      'tuesday': 1,
      'wednesday': 2,
      'thursday': 3,
      'friday': 4,
      'saturday': 5,
      'sunday': 6
    };

    // Comparison function to sort the objects by name property
    const compareDays = (a: any, b: any) => {
      // Convert the name to lowercase for comparison
      const dayA = a.name.toLowerCase();
      const dayB = b.name.toLowerCase();
      return weekOrder[dayA] - weekOrder[dayB];
    };

    return data.sort(compareDays);
  }

  getAllCustomerTypes() {
    const params: RequestHandlerParams = {
      endpoint: this.endpoints.customerTypes,
      method: 'GET',
      apiV3: true
    }

    return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      finalize(() => this.isLoading.set(false))
    );
  }

  getPickUpOption(membershipId: number) {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.base}${this.endpoints.pickUpOption}?membershipId=${membershipId}`,
      method: 'GET',
      apiV3: true,
      showErrorMessage: false
    }

    return this.apiService.handleRequest<any[]>(params).pipe(
      map((data) => this.setUpPickupOptionsResponse(data))
    );
  }

  private setUpPickupOptionsResponse(data: any[]): any {
    // Mapping of days of the week to numeric values
    const weekOrder: any = {
      'monday': 0,
      'tuesday': 1,
      'wednesday': 2,
      'thursday': 3,
      'friday': 4,
      'saturday': 5,
      'sunday': 6
    };

    // Comparison function to sort the objects by name property
    const compareDays = (a: any, b: any) => {
      // Convert the name to lowercase for comparison
      const dayA = a.name.toLowerCase();
      const dayB = b.name.toLowerCase();
      return weekOrder[dayA] - weekOrder[dayB];
    };

    return data.map(p => {
      p.deliveryDays = p.deliveryDays.sort(compareDays);
      return p;
    });
  }

  getAccountSettings() {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.base}${this.endpoints.settings}`,
      method: 'GET',
      apiV3: true
    }
    return this.apiService.handleRequest<ApiResponse<AccountSettings>>(params).pipe(
      map((response: ApiResponse<AccountSettings>) => response.data)
    );
  }

  notifyOOS(body: { email?: string, phone?: string } | null, productId: number) {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.base}${this.endpoints.stockNotification}/${productId}`,
      method: 'POST',
      apiV3: true,
      body
    };

    return this.apiService.handleRequest<ApiResponse<any>>(params);
  }

  getMembershipPermissions(routeToNavigate?: string) {
    const params: RequestHandlerParams = {
      endpoint: this.endpoints.ecommercePermissions,
      method: 'GET',
      apiV3: true
    }

    return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      tap((response) => {
        // Get membersip configurations for update localstorage session and configuration signals:
        const isLimited = response?.data?.isLimited ?? false;
        this.signalsStoreService.isLimitedUser.set(isLimited);
        const config = response?.data?.configuration;
        if (config) {
          const currentSession: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
          if (currentSession?.settings) {

            const requireAddress = config.requireAddress ?? true;
            this.signalsStoreService.isAddressRequired.set(requireAddress);
            const requirePaymentMethod = config.requirePaymentMethod ?? true;

            const currentSettings = currentSession.settings;
            const newSettings = { ...currentSettings, isLimited, requireAddress, requirePaymentMethod }

            const newSession = { ...currentSession, settings: { ...newSettings } };

            this.localStorageService.set(LOCALSTORAGE_KEYS.SESSION, newSession);

          }
        }
        if (routeToNavigate)
          this.reloadPage(routeToNavigate);
      }),
      map((response) => normalizeECommercePermissionsResponse(response.data?.permissions)),
      tap((permissions) => {
        this.signalsStoreService.permissions.set(permissions);
        this.localStorageService.set(LOCALSTORAGE_KEYS.PERMISSIONS, permissions)
      })
    );
  }

  private reloadPage(route: string) {
    this.location.replaceState(route);
    window.location.reload();
  }

  setMustFetchStates() {
    this.mustFetchStatesSubject.next(true);
  }


  /**
   * Full `POST /check-email` result.
   *
   * The endpoint answers two questions at once: is this email taken, and — when
   * it belongs to an explorer who never finished registering — what is their
   * account and address, so the registration can be resumed instead of blocked
   * (DEV-11262). Callers that only care about the first question keep using
   * `validateEmailExistence`.
   */
  checkEmail(email: string) {
    const params: RequestHandlerParams = {
      endpoint: '/check-email',
      apiV3: true,
      body: { email },
      method: 'POST'
    };

    return this.apiService.handleRequest<ApiResponse<EmailExistenceResponse>>(params).pipe(map(res => res.data));
  }

  validateEmailExistence(email: string) {
    return this.checkEmail(email).pipe(map(data => data.emailUsed));
  }

  //#region Signup email verification

  /**
   * Creates (or replaces) the temporary signup record and triggers the
   * verification email. Calling it again for the same address IS the resend:
   * the backend mints a new token and the previous link stops working.
   *
   * `state` is opaque to the backend — stored and handed back verbatim — so the
   * tab opened from the link can rebuild the form it left behind.
   */
  createSignupVerification(email: string, state: any) {
    const params: RequestHandlerParams = {
      endpoint: this.endpoints.signupVerification,
      method: 'POST',
      apiV3: true,
      body: { email, state },
      // The error has to come back in the stream rather than as a toast: a 429
      // is a cooldown, not a failure, and the caller branches on it.
      returnError: true,
      showErrorMessage: false
    };

    return this.apiService.handleRequest<ApiResponse<any>>(params);
  }

  /** Trades the token from the link for the stored record. Idempotent within
   *  the 72h TTL, which is what lets the verified tab survive a refresh. */
  getSignupVerification(token: string) {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.signupVerification}/${token}`,
      method: 'GET',
      apiV3: true,
      returnError: true,
      showErrorMessage: false
    };

    return this.apiService.handleRequest<ApiResponse<any>>(params);
  }

  //#endregion

  //#region Explorer flow (DEV-11262)

  /**
   * Creates the explorer account from the check-address modal and switches the
   * app over to the new session in place. No order is created here — the cart
   * is born with the first `POST /order`, as it is for a customer.
   *
   * The caller must guard against re-entry: two of these in flight produce two
   * accounts for one visitor (DEV-10545).
   */
  createExplorerAccount(body: ExplorerUserPayload) {
    this.isLoading.set(true);

    const params: RequestHandlerParams = {
      endpoint: this.endpoints.explorer,
      method: 'POST',
      apiV3: true,
      body,
      returnError: true,
      showErrorMessage: false
    };

    return this.apiService.handleRequest<ApiResponse<Session>>(params).pipe(
      tap(response => {
        if (response.error) {
          this.#notificationService.show({ text: response.message, type: 'error' });
          return;
        }

        if (!response.data) return;

        // Stored as the server sent it: `settings.isExplorer` and
        // `settings.isAnonymousExplorer` come down with the session, and the
        // three states they encode are the server's to decide, not ours.
        this.localStorageService.set(LOCALSTORAGE_KEYS.SESSION, response.data);
        this.signalsStoreService.intialSetup();
        this.getMembershipPermissions().subscribe();
        this.#klaviyoService.identiyUser();
        this.setExplorerAccountCreated(true);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  /**
   * Turns an anonymous explorer into a registered account.
   *
   * This replaces `create()` for them, and takes the very same body: the
   * account already exists, so `POST /account` would answer 409. The card is
   * *not* part of it — it follows separately through `addPaymentMethod()`, on
   * the step after, exactly as it does for everyone else.
   */
  activateExplorerUser(body: NewAccountPayload) {
    this.isLoading.set(true);

    const params: RequestHandlerParams = {
      method: 'POST',
      endpoint: this.endpoints.activateExplorer,
      body,
      apiV3: true,
      returnError: true,
      showErrorMessage: false
    };

    return this.apiService.handleRequest<ApiResponse<Session>>(params).pipe(
      tap((response) => {
        if (response.error) {
          this.#notificationService.show({ text: response.message, type: 'error' });
          return;
        }

        if (!response.data) return;

        // The explorer's token stops working the instant the account is
        // activated — the backend answers 403 `SESSION_INVALID` to it on every
        // endpoint. Its replacement is in this response, so the whole session
        // is written wholesale rather than patched: the card step, the
        // checkout and everything after depend on the swap happening here.
        this.localStorageService.set(LOCALSTORAGE_KEYS.SESSION, response.data);
        this.signalsStoreService.intialSetup();

        this.localStorageService.set(LOCALSTORAGE_KEYS.FIRST_LOGIN, !!response.data.settings?.firstLogin);
        this.localStorageService.set(LOCALSTORAGE_KEYS.NEW_ACCOUNT, true);

        this.getMembershipPermissions().subscribe();
        this.#klaviyoService.identiyUser();

        // The activation response does not carry the carts, unlike /explorer.
        // `OrderService.getOrder()` is not the way to get them back here: it
        // returns early while the account has no payment method, which is
        // precisely the state activation leaves them in. The Firebase listener
        // is keyed on the account id, which has not changed, so re-syncing it
        // is what keeps the cart on screen through the card step.
        this.signalsStoreService.getAndSyncCart();
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  //#endregion

  applyRetentionCoupon(couponId: number) {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.base}/coupons/retention`,
      method: 'POST',
      apiV3: true,
      body: { id: couponId },
      showErrorMessage: false,
      returnError: true,
    };

    return this.apiService.handleRequest<ApiResponse<any>>(params);
  }

}
